import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useRef } from "react";
import { FiUpload } from "react-icons/fi";

const RentalExportDataXlsx = ({
  exportDataXlsx,
  fileNames,
  startDate,
  endDate,
}) => {
  const dRef = useRef();

  const handleExportXLSX = (e) => {
    e.preventDefault();

    if (!exportDataXlsx || exportDataXlsx.length === 0) {
      console.warn("No data available for export");
      return;
    }

    let worksheet;

    /*
     * ============================================================
     * 1. CREATE WORKSHEET
     * ============================================================
     */

    // If data is already Array of Arrays
    if (
      Array.isArray(exportDataXlsx) &&
      Array.isArray(exportDataXlsx[0])
    ) {
      worksheet = XLSX.utils.aoa_to_sheet(exportDataXlsx);
    }

    // If data is JSON array
    else if (
      Array.isArray(exportDataXlsx) &&
      typeof exportDataXlsx[0] === "object"
    ) {
      const headers = Object.keys(exportDataXlsx[0]);

      const rows = exportDataXlsx.map((obj) =>
        headers.map((header) => obj[header] ?? "")
      );

      worksheet = XLSX.utils.aoa_to_sheet([
        headers,
        ...rows,
      ]);
    }

    else {
      console.warn("Unsupported export data format");
      return;
    }

    /*
     * ============================================================
     * 2. HEADER STYLE
     * ============================================================
     */

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({
        r: 0,
        c: C,
      });

      if (!worksheet[cellAddress]) continue;

      worksheet[cellAddress].s = {
        font: {
          bold: true,
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
      };
    }

    /*
     * ============================================================
     * 3. CELL ALIGNMENT
     * ============================================================
     */

    for (let R = range.s.r + 1; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({
          r: R,
          c: C,
        });

        const cell = worksheet[cellAddress];

        if (!cell) continue;

        cell.s = {
          alignment: {
            vertical: "center",
            wrapText: true,
          },
        };
      }
    }

    /*
     * ============================================================
     * 4. COLUMN WIDTHS
     * ============================================================
     */

    worksheet["!cols"] = [
      { wch: 20 }, // City
      { wch: 12 }, // City ID
      { wch: 22 }, // Vehicle Type
      { wch: 12 }, // 8H 80KM
      { wch: 13 }, // 10H 100KM
      { wch: 12 }, // Extra KM
      { wch: 12 }, // Extra HR
      { wch: 10 }, // Prime
      { wch: 12 }, // Status
    ];

    /*
     * ============================================================
     * 5. FREEZE HEADER ROW
     * ============================================================
     */

    worksheet["!freeze"] = {
      xSplit: 0,
      ySplit: 1,
    };

    /*
     * ============================================================
     * 6. CREATE WORKBOOK
     * ============================================================
     */

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Hour Rental Rate"
    );

    /*
     * ============================================================
     * 7. GENERATE EXCEL FILE
     * ============================================================
     */

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    /*
     * ============================================================
     * 8. CREATE BLOB
     * ============================================================
     */

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    /*
     * ============================================================
     * 9. FILE NAME
     * ============================================================
     */

    const formatFileDate = (date) => {
      if (!date) return "";

      const d = new Date(date);

      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();

      return `${day}-${month}-${year}`;
    };

    const start = formatFileDate(startDate);
    const end = formatFileDate(endDate);

    const finalFileName =
      start && end
        ? `${fileNames} (${start} - ${end}).xlsx`
        : `${fileNames}.xlsx`;

    /*
     * ============================================================
     * 10. DOWNLOAD
     * ============================================================
     */

    saveAs(blob, finalFileName);
  };

  return (
    <div className="flex flex-grow-0">
      <div ref={dRef} className="flex-1">
        <button
          type="button"
          onClick={handleExportXLSX}
          className="border flex justify-center items-center border-gray-300 hover:border-green-400 hover:text-green-400 dark:text-gray-300 cursor-pointer h-10 w-20 rounded-md focus:outline-none min-h-[50px]"
        >
          <FiUpload className="mr-2" />

          <span className="text-xs">
            Export
          </span>
        </button>
      </div>
    </div>
  );
};

export default RentalExportDataXlsx;